// src/pages/Admin/pages/ManajemenSewaKendaraan.jsx
import React, { useState, useEffect } from 'react';
import { sewaKendaraanService } from '../../../services/sewaKendaraanService';
import { useAuth } from '../../Admin/context/AuthContext';
import { getAssetUrl } from '../../../services/api';
import {
    FaPlus, FaEdit, FaTrash, FaSave, FaTimes,
    FaCheckCircle, FaExclamationTriangle, FaEye, FaEyeSlash,
    FaCar, FaMoneyBillWave, FaImage, FaTag, FaUsers
} from 'react-icons/fa';

const ManajemenSewaKendaraan = () => {
    const { user } = useAuth();
    const hasPermission = (p) => {
        if (!user) return false;
        if (user?.roles?.includes('admin')) return true;
        return user?.permissions?.includes(p) || false;
    };

    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [notification, setNotification] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    const [formData, setFormData] = useState({
        nama: '',
        tipe: '',
        kapasitas: '',
        tahun: '',
        fasilitasText: '',
        tarif_per_hari: '',
        tarif_dengan_supir: '',
        foto_emoji: '🚗',
        tersedia: true,
        deskripsi: '',
        order: 0,
    });

    // Foto state
    const [newPhotos, setNewPhotos] = useState([]);          // [{file, label}]
    const [existingPhotos, setExistingPhotos] = useState([]); // [{path, label}]
    const [removePhotos, setRemovePhotos] = useState([]);     // [{path, label}]

    const showNotif = (message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 3000);
    };

    const loadData = async () => {
        setLoading(true);
        const result = await sewaKendaraanService.getList();
        if (result.success) setList(result.data);
        setLoading(false);
    };

    useEffect(() => { loadData(); }, []);

    const handleCreate = () => {
        setEditing(null);
        setFormData({
            nama: '', tipe: '', kapasitas: '', tahun: '',
            fasilitasText: '', tarif_per_hari: '', tarif_dengan_supir: '',
            foto_emoji: '🚗', tersedia: true, deskripsi: '', order: list.length + 1,
        });
        setNewPhotos([]);
        setExistingPhotos([]);
        setRemovePhotos([]);
        setShowForm(true);
    };

    const handleEdit = (item) => {
        setEditing(item);
        setFormData({
            nama: item.nama,
            tipe: item.tipe || '',
            kapasitas: item.kapasitas || '',
            tahun: item.tahun || '',
            fasilitasText: Array.isArray(item.fasilitas) ? item.fasilitas.join(', ') : '',
            tarif_per_hari: item.tarif_per_hari,
            tarif_dengan_supir: item.tarif_dengan_supir || '',
            foto_emoji: item.foto_emoji || '🚗',
            tersedia: item.tersedia,
            deskripsi: item.deskripsi || '',
            order: item.order || 0,
        });
        setNewPhotos([]);

        const rawFotos = Array.isArray(item.fotos) ? item.fotos : [];
        setExistingPhotos(
            rawFotos.map((f) => {
                if (typeof f === 'string') return { path: f, label: '' };
                return { path: f.path || f.url || '', label: f.label || '' };
            })
        );
        setRemovePhotos([]);
        setShowForm(true);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    // Upload multiple foto
    const handlePhotosChange = (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        const valid = [];
        const maxSize = 5 * 1024 * 1024;
        const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

        files.forEach(f => {
            if (!allowed.includes(f.type)) {
                showNotif(`File ${f.name} bukan gambar valid`, 'error');
                return;
            }
            if (f.size > maxSize) {
                showNotif(`File ${f.name} melebihi 5MB`, 'error');
                return;
            }
            valid.push({ file: f, label: '' });
        });

        if (valid.length > 0) setNewPhotos(prev => [...prev, ...valid]);
        e.target.value = '';
    };

    const removeNewPhoto = (index) => {
        setNewPhotos(prev => prev.filter((_, i) => i !== index));
    };

    const updateNewPhotoLabel = (index, label) => {
        setNewPhotos(prev => prev.map((p, i) => (i === index ? { ...p, label } : p)));
    };

    const updateExistingPhotoLabel = (path, label) => {
        setExistingPhotos(prev => prev.map(p => (p.path === path ? { ...p, label } : p)));
    };

    const removeExistingPhoto = (photoObj) => {
        setExistingPhotos(prev => prev.filter(p => p.path !== photoObj.path));
        setRemovePhotos(prev => [...prev, photoObj]);
    };

    const undoRemoveExistingPhoto = (photoObj) => {
        setRemovePhotos(prev => prev.filter(p => p.path !== photoObj.path));
        setExistingPhotos(prev => [...prev, photoObj]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);

        const fd = new FormData();
        fd.append('nama', formData.nama);
        fd.append('tipe', formData.tipe || '');
        fd.append('kapasitas', formData.kapasitas || '');
        fd.append('tahun', formData.tahun || '');
        fd.append('tarif_per_hari', parseFloat(formData.tarif_per_hari) || 0);
        if (formData.tarif_dengan_supir) {
            fd.append('tarif_dengan_supir', parseFloat(formData.tarif_dengan_supir));
        }
        fd.append('foto_emoji', formData.foto_emoji);
        fd.append('tersedia', formData.tersedia ? 1 : 0);
        fd.append('deskripsi', formData.deskripsi || '');
        fd.append('order', parseInt(formData.order) || 0);

        // Fasilitas
        const fasList = formData.fasilitasText.split(',').map(s => s.trim()).filter(Boolean);
        fasList.forEach((f, i) => fd.append(`fasilitas[${i}]`, f));

        // Foto baru + label
        newPhotos.forEach((p) => {
            fd.append('fotos[]', p.file);
            fd.append('foto_labels[]', p.label || '');
        });

        // Foto lama
        existingPhotos.forEach((p, i) => {
            fd.append(`existing_fotos[${i}][path]`, p.path);
            fd.append(`existing_fotos[${i}][label]`, p.label || '');
        });

        // Hapus
        removePhotos.forEach((p, i) => {
            fd.append(`remove_fotos[${i}]`, p.path);
        });

        let result;
        if (editing) {
            result = await sewaKendaraanService.update(editing.id, fd);
        } else {
            result = await sewaKendaraanService.create(fd);
        }

        if (result.success) {
            showNotif(result.message || 'Berhasil disimpan');
            setShowForm(false);
            loadData();
        } else {
            showNotif(result.message || 'Gagal menyimpan', 'error');
        }
        setSubmitting(false);
    };

    const handleDelete = async () => {
        if (!deleteConfirm) return;
        const result = await sewaKendaraanService.delete(deleteConfirm.id);
        if (result.success) {
            showNotif('Kendaraan berhasil dihapus');
            loadData();
        }
        setDeleteConfirm(null);
    };

    const handleToggle = async (item) => {
        const result = await sewaKendaraanService.toggle(item.id);
        if (result.success) {
            showNotif(result.message);
            loadData();
        }
    };

    const getFotoUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('http')) return path;
        return getAssetUrl(path);
    };

    const getCoverLabel = (item) => {
        const rawFotos = Array.isArray(item.fotos) ? item.fotos : [];
        if (rawFotos.length === 0) return '';
        const first = rawFotos[0];
        if (typeof first === 'string') return '';
        return first.label || '';
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-2xl font-bold text-[#0F4C3A]">Manajemen Sewa Kendaraan</h2>
                    <p className="text-gray-600">Kelola daftar kendaraan dinas, foto, tarif, dan ketersediaan</p>
                </div>
                {hasPermission('sewa-kendaraan.create') && (
                    <button onClick={handleCreate} className="btn-primary flex items-center">
                        <FaPlus className="mr-2" /> Tambah Kendaraan
                    </button>
                )}
            </div>

            {notification && (
                <div className={`mb-4 p-4 rounded-lg flex items-center ${notification.type === 'success'
                    ? 'bg-green-50 border border-green-200 text-green-700'
                    : 'bg-red-50 border border-red-200 text-red-700'}`}>
                    {notification.type === 'success' ? <FaCheckCircle className="mr-2" /> : <FaExclamationTriangle className="mr-2" />}
                    {notification.message}
                </div>
            )}

            {loading ? (
                <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2E7D32] mx-auto"></div>
                </div>
            ) : list.length === 0 ? (
                <div className="bg-white rounded-xl p-12 text-center shadow-sm">
                    <FaCar className="text-6xl text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Belum ada data kendaraan</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {list.map(item => {
                        const fotos = item.foto_urls || [];
                        const coverUrl = fotos[0];
                        const coverLabel = getCoverLabel(item);

                        return (
                            <div key={item.id} className="bg-white rounded-xl shadow-sm overflow-hidden">
                                <div className="relative h-48 bg-gradient-to-br from-[#1E3A8A] to-[#0D9488] flex items-center justify-center text-white">
                                    {coverUrl ? (
                                        <img
                                            src={coverUrl}
                                            alt={item.nama}
                                            className="w-full h-full object-cover"
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                    ) : (
                                        <span className="text-6xl">{item.foto_emoji || '🚗'}</span>
                                    )}

                                    {coverLabel && (
                                        <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded-lg max-w-[70%] truncate flex items-center gap-1">
                                            <FaTag size={9} /> {coverLabel}
                                        </div>
                                    )}

                                    {fotos.length > 1 && (
                                        <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded-full flex items-center">
                                            <FaImage className="mr-1" size={10} /> {fotos.length} foto
                                        </div>
                                    )}

                                    {!item.tersedia && (
                                        <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                                            Tidak Tersedia
                                        </div>
                                    )}

                                    <div className="absolute top-2 left-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
                                        #{item.order}
                                    </div>
                                </div>

                                <div className="p-4">
                                    <h3 className="font-bold text-[#1E3A8A] mb-1">{item.nama}</h3>
                                    {item.tipe && <p className="text-xs text-gray-500 mb-2">{item.tipe}</p>}

                                    <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                                        <div className="bg-gray-50 p-2 rounded">
                                            <p className="font-semibold">{item.kapasitas || '-'}</p>
                                            <p className="text-gray-500">Kapasitas</p>
                                        </div>
                                        <div className="bg-gray-50 p-2 rounded">
                                            <p className="font-semibold">{item.tahun || '-'}</p>
                                            <p className="text-gray-500">Tahun</p>
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap gap-1 mb-3">
                                        {Array.isArray(item.fasilitas) && item.fasilitas.slice(0, 4).map((f, i) => (
                                            <span key={i} className="bg-green-50 text-green-700 text-xs px-2 py-0.5 rounded-full">
                                                {f}
                                            </span>
                                        ))}
                                    </div>

                                    <p className="text-lg font-bold text-[#0D9488] mb-3">
                                        Rp {Number(item.tarif_per_hari).toLocaleString('id-ID')}
                                        <span className="text-xs text-gray-400 font-normal">/hari</span>
                                    </p>

                                    <div className="flex justify-end gap-1 pt-3 border-t">
                                        {hasPermission('sewa-kendaraan.update') && (
                                            <button
                                                onClick={() => handleToggle(item)}
                                                className={`p-2 rounded-lg ${item.tersedia ? 'text-green-500 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-50'}`}
                                                title={item.tersedia ? 'Nonaktifkan' : 'Aktifkan'}
                                            >
                                                {item.tersedia ? <FaEye size={14} /> : <FaEyeSlash size={14} />}
                                            </button>
                                        )}
                                        {hasPermission('sewa-kendaraan.update') && (
                                            <button onClick={() => handleEdit(item)} className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg" title="Edit">
                                                <FaEdit size={14} />
                                            </button>
                                        )}
                                        {hasPermission('sewa-kendaraan.delete') && (
                                            <button onClick={() => setDeleteConfirm(item)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg" title="Hapus">
                                                <FaTrash size={14} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* FORM MODAL */}
            {showForm && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl max-w-3xl w-full my-8 max-h-[calc(100vh-4rem)] flex flex-col shadow-2xl">
                        <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white rounded-t-2xl z-10">
                            <h3 className="text-xl font-bold text-[#0F4C3A]">
                                {editing ? 'Edit Kendaraan' : 'Tambah Kendaraan'}
                            </h3>
                            <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                                <FaTimes size={20} />
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto flex-1">
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Nama Kendaraan *</label>
                                        <input
                                            type="text" name="nama" value={formData.nama}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Tipe</label>
                                        <input
                                            type="text" name="tipe" value={formData.tipe}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                            placeholder="Contoh: Toyota Innova Zenix"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Kapasitas</label>
                                        <input
                                            type="text" name="kapasitas" value={formData.kapasitas}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                            placeholder="Contoh: 7 orang"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Tahun</label>
                                        <input
                                            type="text" name="tahun" value={formData.tahun}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                            placeholder="Contoh: 2024"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Tarif per Hari (Rp) *</label>
                                        <input
                                            type="number" name="tarif_per_hari" value={formData.tarif_per_hari}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Tarif Dengan Supir (Rp)</label>
                                        <input
                                            type="number" name="tarif_dengan_supir" value={formData.tarif_dengan_supir}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Fasilitas (pisahkan dengan koma)</label>
                                    <input
                                        type="text" name="fasilitasText" value={formData.fasilitasText}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                        placeholder="AC, Audio System, GPS"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium mb-1">Deskripsi</label>
                                    <textarea
                                        name="deskripsi" value={formData.deskripsi}
                                        onChange={handleInputChange}
                                        rows="3"
                                        className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Emoji (fallback)</label>
                                        <input
                                            type="text" name="foto_emoji" value={formData.foto_emoji}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                            placeholder="🚗"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Urutan</label>
                                        <input
                                            type="number" name="order" value={formData.order}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2E7D32]"
                                        />
                                    </div>
                                </div>

                                {/* FOTO */}
                                <div>
                                    <label className="block text-sm font-medium mb-2">
                                        <FaImage className="inline mr-1" /> Foto Kendaraan (Multiple + Keterangan)
                                    </label>

                                    <label
                                        htmlFor="fotos-upload"
                                        className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#2E7D32] hover:bg-green-50/30 transition-colors"
                                    >
                                        <FaPlus className="text-2xl text-gray-400 mb-2" />
                                        <p className="text-sm text-gray-500">Klik untuk pilih foto</p>
                                        <p className="text-xs text-gray-400 mt-1">JPG, PNG, WEBP • Maks 5MB per file</p>
                                        <input
                                            id="fotos-upload"
                                            type="file"
                                            accept="image/jpeg,image/jpg,image/png,image/webp"
                                            multiple
                                            onChange={handlePhotosChange}
                                            className="hidden"
                                        />
                                    </label>

                                    {/* Existing */}
                                    {existingPhotos.length > 0 && (
                                        <div className="mt-4">
                                            <p className="text-xs font-semibold text-gray-500 mb-2">
                                                Foto Tersimpan ({existingPhotos.length})
                                            </p>
                                            <div className="space-y-3">
                                                {existingPhotos.map((photo, idx) => (
                                                    <div key={`ex-${idx}`} className="flex gap-3 p-3 bg-gray-50 border rounded-lg">
                                                        <div className="relative flex-shrink-0 w-24 h-24">
                                                            <img
                                                                src={getFotoUrl(photo.path)}
                                                                alt=""
                                                                className="w-full h-full object-cover rounded-lg border"
                                                                onError={(e) => { e.target.src = 'https://via.placeholder.com/150?text=No+Image'; }}
                                                            />
                                                            {idx === 0 && (
                                                                <div className="absolute top-1 left-1 bg-blue-500 text-white text-[10px] px-1.5 py-0.5 rounded">
                                                                    Utama
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                                                <FaTag className="inline mr-1" size={9} /> Keterangan Foto
                                                            </label>
                                                            <input
                                                                type="text"
                                                                value={photo.label}
                                                                onChange={(e) => updateExistingPhotoLabel(photo.path, e.target.value)}
                                                                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#2E7D32]"
                                                                placeholder="Contoh: Tampak Depan"
                                                                maxLength={100}
                                                            />
                                                            <div className="flex justify-end mt-2">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => removeExistingPhoto(photo)}
                                                                    className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                                                                >
                                                                    <FaTrash size={10} /> Hapus Foto
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* New */}
                                    {newPhotos.length > 0 && (
                                        <div className="mt-4">
                                            <p className="text-xs font-semibold text-gray-500 mb-2">
                                                Foto Baru ({newPhotos.length})
                                            </p>
                                            <div className="space-y-3">
                                                {newPhotos.map((photo, idx) => (
                                                    <div key={`new-${idx}`} className="flex gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                                                        <div className="relative flex-shrink-0 w-24 h-24">
                                                            <img
                                                                src={URL.createObjectURL(photo.file)}
                                                                alt=""
                                                                className="w-full h-full object-cover rounded-lg border"
                                                            />
                                                            <div className="absolute top-1 left-1 bg-green-500 text-white text-[10px] px-1.5 py-0.5 rounded">
                                                                Baru
                                                            </div>
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-xs text-gray-500 mb-1 truncate">
                                                                {photo.file.name} ({(photo.file.size / 1024).toFixed(1)} KB)
                                                            </p>
                                                            <label className="block text-xs font-medium text-gray-600 mb-1">
                                                                <FaTag className="inline mr-1" size={9} /> Keterangan Foto
                                                            </label>
                                                            <input
                                                                type="text"
                                                                value={photo.label}
                                                                onChange={(e) => updateNewPhotoLabel(idx, e.target.value)}
                                                                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#2E7D32]"
                                                                placeholder="Contoh: Interior"
                                                                maxLength={100}
                                                            />
                                                            <div className="flex justify-end mt-2">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => removeNewPhoto(idx)}
                                                                    className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                                                                >
                                                                    <FaTrash size={10} /> Batal Pilih
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Removed */}
                                    {removePhotos.length > 0 && (
                                        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                                            <p className="text-xs text-red-700 mb-2">
                                                {removePhotos.length} foto akan dihapus saat disimpan.
                                            </p>
                                            <div className="flex flex-wrap gap-2">
                                                {removePhotos.map((photo, idx) => (
                                                    <button
                                                        key={`rm-${idx}`}
                                                        type="button"
                                                        onClick={() => undoRemoveExistingPhoto(photo)}
                                                        className="text-xs text-red-600 hover:text-red-800 underline"
                                                    >
                                                        Batalkan hapus {photo.label ? `"${photo.label}"` : ''}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <label className="flex items-center p-3 bg-gray-50 rounded-lg cursor-pointer">
                                    <input
                                        type="checkbox" name="tersedia" checked={formData.tersedia}
                                        onChange={handleInputChange}
                                        className="mr-2 text-[#2E7D32] focus:ring-[#2E7D32]"
                                    />
                                    <span className="text-sm font-medium">Kendaraan Tersedia</span>
                                </label>

                                <div className="flex justify-end gap-3 pt-4 border-t sticky bottom-0 bg-white">
                                    <button type="button" onClick={() => setShowForm(false)} className="px-6 py-2.5 border rounded-lg">
                                        Batal
                                    </button>
                                    <button type="submit" disabled={submitting} className="flex items-center bg-[#2E7D32] text-white px-6 py-2.5 rounded-lg disabled:opacity-50">
                                        {submitting ? 'Menyimpan...' : <><FaSave className="mr-2" /> Simpan</>}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirm */}
            {deleteConfirm && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full p-6 text-center">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <FaTrash className="text-3xl text-red-500" />
                        </div>
                        <h3 className="text-xl font-bold mb-2">Hapus Kendaraan?</h3>
                        <p className="text-gray-600 mb-6">
                            Anda akan menghapus <strong>{deleteConfirm.nama}</strong>
                        </p>
                        <div className="flex justify-center gap-3">
                            <button onClick={() => setDeleteConfirm(null)} className="px-6 py-2.5 border rounded-lg">Batal</button>
                            <button onClick={handleDelete} className="px-6 py-2.5 bg-red-500 text-white rounded-lg">Hapus</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManajemenSewaKendaraan;